import styled from '@emotion/styled'
import { SPACING } from 'constants/spacing.constants'
import { WilsonChatMessageAI } from './WilsonChatMessageAI'
import { WilsonChatMessageUser } from './WilsonChatMessageUser'
import { WilsonChatPrompt, WilsonChatPromptRef } from './WilsonChatPrompt'
import Typography from 'components/core/Typography'
import { pxToRem } from 'utils/rem.utils'
import {
    UIEventHandler,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import debounce from 'lodash.debounce'
import {
    CHAT_ACCEPTED_FILE_TYPES,
    CHAT_MAX_FILE_SIZE_MB,
    CHAT_SCROLL_DOWN_THRESHOLD,
} from '../wilsonChat.constants'
import { DESIGN_TOKENS } from 'constants/color.constants'
import { ArrowDown } from 'lucide-react'
import { CircularProgress } from '@mui/material'
import { FORM_WIDGETS } from '../widgets/widget.constants'
import {
    WilsonChatMessage,
    WilsonCustomerChatMessage,
    WilsonPromptSuggestion,
} from '../wilsonChat.types'
import Row from 'components/core/Row'
import { cn } from 'utils/cn.utils'
import { useIsMobile } from 'hooks/useIsMobile'
import { MOBILE_BREAKPOINT } from 'constants/app.constants'
import { validateFile } from 'utils/file.utils'
import { useDropzone } from 'react-dropzone'
import { FileDropzoneOverlay } from 'components/composite/FileDropzoneOverlay'
import { scrollbarStyles } from 'components/core/Scrollbar'

const StyledWilsonChat = styled.div`
    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    padding-bottom: ${SPACING.p3};
    display: flex;
    flex-direction: column;
    height: 100%;
    gap: ${SPACING.p1};

    .wilsonChat__messages {
        flex-grow: 1;
        overflow: hidden;
        position: relative;

        .wilsonChat__messages__loading {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
        }

        .wilsonChat__messages__scrollDownBtn {
            position: absolute;
            bottom: ${pxToRem(60)};
            right: 50%;
            transform: translateX(50%);
            border-radius: ${DESIGN_TOKENS.borderRadius.round};
            width: ${pxToRem(30)};
            height: ${pxToRem(30)};
            display: flex;
            justify-content: center;
            align-items: center;
            animation: fadeIn 0.1s ease-in-out;
            border: 1px solid ${DESIGN_TOKENS.border.input};
            background: ${DESIGN_TOKENS.background.light};
            color: ${DESIGN_TOKENS.text.background};

            &:active {
                transform: translateX(50%) scale(0.98);
            }
        }

        .wilsonChat__messages__promptSuggestions {
            position: absolute;
            bottom: ${SPACING.sm};
            width: 100%;
            animation: fadeIn 0.1s ease-in-out;

            .wilsonChat__messages__promptSuggestions__button {
                border-radius: ${DESIGN_TOKENS.borderRadius.round};
                display: flex;
                justify-content: center;
                align-items: center;
                gap: ${SPACING.sm};
                animation: fadeIn 0.1s ease-in-out;
                border: 1px solid ${DESIGN_TOKENS.border.input};
                color: ${DESIGN_TOKENS.text.foreground};
                background: ${DESIGN_TOKENS.background.primary};
                border-radius: ${pxToRem(22)};
                padding-top: ${pxToRem(6)};
                padding-bottom: ${pxToRem(6)};
                padding-left: ${pxToRem(10)};
                padding-right: ${pxToRem(10)};
                transition: ${DESIGN_TOKENS.transition.inputHover};

                &:active {
                    transform: scale(0.98);
                }

                &:hover {
                    background: ${DESIGN_TOKENS.hover.secondary};
                }
            }
        }

        .wilsonChat__messages__scrollable {
            z-index: 1;
            display: flex;
            flex-direction: column;
            gap: ${SPACING.p4};
            height: 100%;
            overflow-y: auto;
            padding-left: ${SPACING.p3};
            padding-right: ${SPACING.p3};
            padding-top: ${SPACING.p3};

            ${scrollbarStyles}

            > *:last-child {
                padding-bottom: ${SPACING.p3};
            }

            &.wilsonChat__messages__scrollable--hasPromptSuggestions {
                > *:last-child {
                    padding-bottom: ${pxToRem(60)};
                }
            }
        }
    }

    .wilsonChat__footer {
        padding-left: ${SPACING.p3};
        padding-right: ${SPACING.p3};
        display: flex;
        flex-direction: column;
        gap: ${SPACING.p1};
    }

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
        padding-bottom: ${SPACING.p2};
    }
`
const emptyArr: [] = []
export const WilsonChat = ({
    isLoading,
    shouldFocusInputOnMount = true,
    hasMessageHint = true,
    hasPromptInput = true,
    hasAttachments = true,
    chatId,
    messageHint = 'Wilson may make mistakes. Please double-check responses.',
    promptPlaceholder,
    promptControls,
    promptSuggestions,
    messages: propsMessages = emptyArr,
    onSendMessage,
}: {
    isLoading: boolean
    shouldFocusInputOnMount?: boolean
    hasMessageHint?: boolean
    hasPromptInput?: boolean
    hasAttachments?: boolean
    chatId: string
    messageHint?: string
    promptPlaceholder?: string
    promptControls?: React.ReactNode
    promptSuggestions?: WilsonPromptSuggestion[]
    messages: WilsonChatMessage[]
    onSendMessage: (args: {
        message: string
        files?: File[]
        payload?: WilsonCustomerChatMessage['payload']
    }) => void
}) => {
    const didInitialScrollRef = useRef(false)
    const scrollableRef = useRef<HTMLDivElement>(null)
    const promptInputRef = useRef<WilsonChatPromptRef>(null)
    const [showScrollDown, setShowScrollBack] = useState(false)
    const [promptFiles, setPromptFiles] = useState<File[]>([])
    const isMobile = useIsMobile()

    const _onDropFiles = useCallback((acceptedFiles: File[]) => {
        const validFiles = acceptedFiles.filter((file) =>
            validateFile({
                maxFileSizeMb: CHAT_MAX_FILE_SIZE_MB,
                acceptedFileTypes: CHAT_ACCEPTED_FILE_TYPES,
                file,
            })
        )
        setPromptFiles((prev) => [...prev, ...validFiles])
    }, [])

    const _onSendPromptMessage = useCallback(
        ({ message }: { message: string }) => {
            onSendMessage({
                message,
                files: promptFiles,
            })
            setPromptFiles([])
        },
        [onSendMessage, promptFiles]
    )

    const {
        isDragActive: isFileDropzoneActive,
        getRootProps: getFileDropzoneRootProps,
        getInputProps: getFileDropzoneInputProps,
    } = useDropzone({
        disabled: !hasAttachments,
        noClick: true,
        onDrop: _onDropFiles,
    })

    /**
     * If customers generated responses through a widget we want to
     * make sure that the payload in their response is mapped to the
     * original widget. We also want to filter out widget responses
     * from being visible in the UI.
     */
    const messages = useMemo(() => {
        const widgetReplyDataMap = propsMessages.reduce((acc, message) => {
            const { type, payload } = message
            if (
                payload &&
                (type === 'customer' || type === 'ops') &&
                payload.widgetData &&
                payload.replyToWidget &&
                payload.replyToMessageId
            ) {
                const widgetReplyDataKey = `${payload.replyToWidget}-${payload.replyToMessageId}`
                acc[widgetReplyDataKey] = payload.widgetData
            }
            return acc
        }, {} as Record<string, object>)

        const messagesWithFixedPayloads = propsMessages.map((message) => {
            const { type, payload } = message

            if (type === 'ai' && payload?.widget) {
                const widgetReplyDataKey = `${payload?.widget}-${message.id}`
                const widgetReplyData = widgetReplyDataMap[widgetReplyDataKey]
                return {
                    ...message,
                    payload: {
                        ...(payload ?? {}),
                        widgetData: widgetReplyData ?? payload.widgetData,
                    },
                }
            }

            return message
        })

        const visibleMessages = messagesWithFixedPayloads.filter((message) => {
            // Filter out messages that have widget responses syntax.
            const { type, content } = message
            // @TODO: val extract into a util.
            if (
                (type === 'customer' || type === 'ops') &&
                content.startsWith('[') &&
                content.endsWith(']')
            ) {
                return false
            }
            return true
        })

        return visibleMessages
    }, [propsMessages])

    const prevMessagesRef = useRef(messages)

    const hasActiveFormWidget = useMemo(() => {
        const lastMessage = messages[messages.length - 1]
        if (!lastMessage) {
            return false
        }
        const { type, payload } = lastMessage
        return (
            type === 'ai' &&
            typeof payload?.widget === 'string' &&
            FORM_WIDGETS.includes(payload?.widget)
        )
    }, [messages])

    const isHandlingMessage = useMemo(() => {
        const lastMessage = messages[messages.length - 1]
        if (!lastMessage) {
            return false
        }
        return lastMessage.type === 'ai' && lastMessage.isPending
    }, [messages])

    const onSmoothScrollDown = useCallback(() => {
        if (!scrollableRef.current) {
            return
        }

        const container = scrollableRef.current
        const lastMessage = container.lastElementChild as HTMLElement

        container.scrollTo({
            top: lastMessage.offsetTop + lastMessage.offsetHeight,
            behavior: 'smooth',
        })
    }, [])

    const onScroll: UIEventHandler<HTMLDivElement> = useMemo(
        () =>
            debounce((e) => {
                const container = e.target as HTMLDivElement
                const scroll =
                    container.scrollHeight -
                    container.scrollTop -
                    container.clientHeight

                setShowScrollBack(scroll > CHAT_SCROLL_DOWN_THRESHOLD)
            }, 150),
        []
    )

    useEffect(() => {
        if (!scrollableRef.current) {
            return
        }

        /**
         * Instant-scroll to the bottom of the chat once right after the messages are loaded.
         */
        if (messages.length && !didInitialScrollRef.current) {
            didInitialScrollRef.current = true
            scrollableRef.current.scrollTop = scrollableRef.current.scrollHeight
            return
        }

        /**
         * Smooth-scroll to the bottom of the chat once a new message is added.
         */
        if (
            didInitialScrollRef.current &&
            prevMessagesRef.current &&
            messages
        ) {
            const prevMessages = prevMessagesRef.current
            const lastPrevMessage = prevMessages?.[prevMessages?.length - 1]
            const lastMessage = messages[messages.length - 1]
            if (lastMessage?.id !== lastPrevMessage?.id) {
                setTimeout(() => {
                    onSmoothScrollDown()
                }, 100)
            }
            prevMessagesRef.current = messages
        }
    }, [messages, onSmoothScrollDown])

    useEffect(() => {
        /**
         * Do not focus on the prompt input when the chat is mounted on mobile.
         * This is because the keyboard will automatically open on mobile and
         * obscure the chat messages.
         */
        if (isMobile) {
            return
        }

        /**
         * Focus on the prompt input when the chat is mounted.
         */
        if (shouldFocusInputOnMount) {
            promptInputRef.current?.focus()
        }
    }, [isMobile, shouldFocusInputOnMount])

    const showPromptSuggestions =
        !!promptSuggestions && !hasActiveFormWidget && !isHandlingMessage

    const fileDropzoneRootProps = getFileDropzoneRootProps()

    return (
        <StyledWilsonChat
            // Providing only the props we need explicitly.
            // Looks like some of the props like onClick (which we do not need)
            // are interfering with the event bubbling on buttons in the chat.
            onDragEnter={fileDropzoneRootProps.onDragEnter}
            onDragLeave={fileDropzoneRootProps.onDragLeave}
            onDragOver={fileDropzoneRootProps.onDragOver}
            onDrop={fileDropzoneRootProps.onDrop}
            ref={fileDropzoneRootProps.ref}
        >
            {isFileDropzoneActive && <FileDropzoneOverlay />}
            <input
                {...getFileDropzoneInputProps()}
                style={{ display: 'none' }}
            />
            <div className="wilsonChat__messages">
                {isLoading && (
                    <div className="wilsonChat__messages__loading">
                        <CircularProgress
                            size={32}
                            style={{
                                color: DESIGN_TOKENS.text.mutedForeground,
                            }}
                        />
                    </div>
                )}
                <div
                    ref={scrollableRef}
                    className={cn('wilsonChat__messages__scrollable', {
                        'wilsonChat__messages__scrollable--hasPromptSuggestions':
                            showPromptSuggestions,
                    })}
                    onScroll={onScroll}
                >
                    {messages.map((message) => {
                        if (message.type === 'ai') {
                            return (
                                <WilsonChatMessageAI
                                    // We do not want the pending message to re-mount after the request is complete
                                    // to prevent any jitter or flickering. Using "pendingRequestId" as a key because
                                    // it is stable between the optimistic message and re-fetched message.
                                    key={message.pendingRequestId || message.id}
                                    id={message.id}
                                    chatId={chatId}
                                    createdAt={message.createdAt}
                                    isPending={!!message.isPending}
                                    pendingRequestId={message.pendingRequestId}
                                    content={message.content ?? ''}
                                    payload={message.payload}
                                    onSendMessage={onSendMessage}
                                    onScrollDown={onSmoothScrollDown}
                                />
                            )
                        } else if (
                            message.type === 'ops' ||
                            message.type === 'customer'
                        ) {
                            return (
                                <WilsonChatMessageUser
                                    key={message.id}
                                    createdAt={message.createdAt}
                                    content={message.content ?? ''}
                                    name={message.name}
                                    attachments={message.attachments}
                                />
                            )
                        }
                        return null
                    })}
                </div>
                {showScrollDown && (
                    <button
                        className="wilsonChat__messages__scrollDownBtn"
                        onClick={onSmoothScrollDown}
                    >
                        <ArrowDown
                            size={18}
                            color={DESIGN_TOKENS.text.background}
                        />
                    </button>
                )}
                {showPromptSuggestions && (
                    <Row
                        gap="p1"
                        justifyContent="center"
                        className="wilsonChat__messages__promptSuggestions"
                    >
                        {promptSuggestions.map((suggestion, idx) => (
                            <button
                                className="wilsonChat__messages__promptSuggestions__button"
                                key={idx}
                                onClick={suggestion.onClick}
                            >
                                {suggestion.renderIcon?.({
                                    size: 16,
                                    color: DESIGN_TOKENS.text.foreground,
                                })}
                                <Typography
                                    isBold
                                    variant="caption"
                                    color="primary"
                                >
                                    {suggestion.label}
                                </Typography>
                            </button>
                        ))}
                    </Row>
                )}
            </div>
            {(hasPromptInput || hasMessageHint) && (
                <div className="wilsonChat__footer">
                    {hasPromptInput && (
                        <WilsonChatPrompt
                            hasActiveFormWidget={hasActiveFormWidget}
                            isHandlingMessage={isHandlingMessage}
                            hasAttachments={hasAttachments}
                            ref={promptInputRef}
                            placeholder={promptPlaceholder}
                            controls={promptControls}
                            files={promptFiles}
                            onSubmit={_onSendPromptMessage}
                            onSetFiles={setPromptFiles}
                        />
                    )}
                    {hasMessageHint && (
                        <Typography
                            variant="caption"
                            color="secondary"
                            textAlign="end"
                        >
                            {messageHint}
                        </Typography>
                    )}
                </div>
            )}
        </StyledWilsonChat>
    )
}
